/**
 * Product Addons
 * --------------
 * For each `[data-product-addons-block]` on the product page:
 *
 * Two modes are supported (via `data-source-mode`):
 *
 *  - "single"   : One fixed addon product. Optionally matches the main
 *                 product's size to resolve the addon variant.
 *  - "metafield": The list of addon products comes from the MAIN product's
 *                 selected variant metafield (e.g. custom.muuntopperny — a
 *                 product list). Tiles show / hide as the main variant
 *                 changes; the selected tile resolves to its variant whose
 *                 size option matches the main product's size.
 *
 * Both modes write the resolved addon variant id to
 * `block.dataset.resolvedVariantId`, which `product-form.js` reads when
 * bundling the cart payload.
 */
(function () {
  'use strict';

  function normalizeSize(str) {
    if (!str) return '';
    var nums = String(str).match(/\d+/g);
    if (nums && nums.length >= 2) {
      return (nums[0] + 'x' + nums[1]).toLowerCase();
    }
    return String(str).toLowerCase().replace(/\s+/g, '');
  }

  function getMainVariantId() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    var idInput = form.querySelector('[name="id"]');
    if (idInput && idInput.value) return idInput.value;

    var sel = document.querySelector('select[name="id"]');
    if (sel && sel.value) return sel.value;

    var radio = document.querySelector('input[name="id"]:checked');
    if (radio && radio.value) return radio.value;

    return null;
  }

  var variantCache = {};
  function fetchVariant(variantId) {
    if (!variantId) return Promise.resolve(null);
    if (variantCache[variantId]) return Promise.resolve(variantCache[variantId]);
    return fetch('/variants/' + variantId + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (v) { if (v) variantCache[variantId] = v; return v; })
      .catch(function () { return null; });
  }

  function getMainVariantSize(variant) {
    if (!variant) return null;
    var opts = [variant.option1, variant.option2, variant.option3];
    for (var i = 0; i < opts.length; i++) {
      var v = opts[i];
      if (v && /\d+\s*[xX×]\s*\d+/.test(v)) return v;
    }
    return null;
  }

  function ProductAddonsBlock(el) {
    this.el = el;
    this.blockId = el.dataset.blockId;
    this.mode = el.dataset.sourceMode || 'single';
    this.sizeInfoEl = el.querySelector('[data-addon-size-info]');
    this.inputs = el.querySelectorAll('[data-addon-input]');

    if (this.mode === 'metafield') {
      try {
        this.visibilityMap = JSON.parse(el.dataset.visibilityMap || '{}');
      } catch (e) {
        this.visibilityMap = {};
      }
      this.tiles = el.querySelectorAll('[data-addon-tile]');
      this.allowSkip = el.dataset.allowSkip === 'true';
    } else {
      this.matchSize = el.dataset.matchSize === 'true';
      this.sizeIndex = parseInt(el.dataset.sizeIndex, 10) || 0;
      this.selectableIndex = parseInt(el.dataset.selectableIndex, 10) || 0;
      try {
        this.variants = JSON.parse(el.dataset.variants || '[]');
      } catch (e) {
        this.variants = [];
      }
    }

    this.bind();
    this.sync();
  }

  ProductAddonsBlock.prototype.bind = function () {
    var self = this;
    this.inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var done = self.mode === 'metafield'
          ? self.syncMetafieldSelection()
          : self.syncSingle();
        Promise.resolve(done).then(updateBundlePrice);
      });
    });
  };

  ProductAddonsBlock.prototype.getCheckedInput = function () {
    return this.el.querySelector('[data-addon-input]:checked');
  };

  // Discounted price (in cents) of the currently-selected addon, or 0 when
  // skipped / nothing valid is selected.
  ProductAddonsBlock.prototype.getSelectedPriceCents = function () {
    var checked = this.getCheckedInput();
    if (!checked || checked.dataset.addonSkip === 'true') return 0;
    var tile = checked.closest('[data-addon-tile]');
    if (!tile || tile.hidden) return 0;
    var cents = parseInt(tile.dataset.tilePrice, 10);
    return isNaN(cents) ? 0 : cents;
  };

  ProductAddonsBlock.prototype.setResolvedVariantId = function (variantId) {
    var input = this.getCheckedInput();
    if (input) {
      if (variantId) input.dataset.addonVariantId = variantId;
      else delete input.dataset.addonVariantId;
    }
    if (variantId) this.el.dataset.resolvedVariantId = variantId;
    else this.el.removeAttribute('data-resolved-variant-id');
  };

  ProductAddonsBlock.prototype.updateSizeInfo = function (state, mainSize) {
    if (!this.sizeInfoEl) return;
    if (state === 'matched' && mainSize) {
      this.sizeInfoEl.innerHTML = 'Size: <strong>' + mainSize + '</strong>';
      this.sizeInfoEl.dataset.state = 'matched';
    } else if (state === 'missing' && mainSize) {
      this.sizeInfoEl.innerHTML = 'Not available in <strong>' + mainSize + '</strong>';
      this.sizeInfoEl.dataset.state = 'missing';
    } else {
      this.sizeInfoEl.innerHTML = '';
      this.sizeInfoEl.dataset.state = '';
    }
  };

  ProductAddonsBlock.prototype.sync = function () {
    if (this.mode === 'metafield') return this.syncMetafield();
    return this.syncSingle();
  };

  // ---------- METAFIELD MODE ----------

  ProductAddonsBlock.prototype.syncMetafield = function () {
    var mainVariantId = getMainVariantId();
    var allowed = (mainVariantId && this.visibilityMap[mainVariantId]) || [];
    var allowedSet = {};
    allowed.forEach(function (id) { allowedSet[String(id)] = true; });

    var firstVisible = null;
    this.tiles.forEach(function (tile) {
      var pid = String(tile.dataset.productId);
      var visible = !!allowedSet[pid];
      tile.hidden = !visible;
      if (visible && !firstVisible) firstVisible = tile;
    });

    var checked = this.getCheckedInput();
    var checkedTile = checked ? checked.closest('[data-addon-tile]') : null;
    var checkedIsValid = false;
    if (checked) {
      if (checked.dataset.addonSkip === 'true') {
        checkedIsValid = this.allowSkip;
      } else if (checkedTile && !checkedTile.hidden) {
        checkedIsValid = true;
      }
    }

    if (!checkedIsValid) {
      var newChoice = null;
      if (this.allowSkip) {
        newChoice = this.el.querySelector('[data-addon-skip="true"]');
      } else if (firstVisible) {
        newChoice = firstVisible.querySelector('input[type="radio"]');
      }
      if (newChoice) newChoice.checked = true;
    }

    return this.syncMetafieldSelection();
  };

  ProductAddonsBlock.prototype.syncMetafieldSelection = function () {
    var self = this;
    var checked = this.getCheckedInput();

    if (!checked || checked.dataset.addonSkip === 'true') {
      this.setResolvedVariantId(null);
      this.updateSizeInfo('', null);
      return Promise.resolve();
    }

    var tile = checked.closest('[data-addon-tile]');
    // A hidden tile means the checked addon isn't valid for the current main
    // variant (e.g. the shopper picked an addon, then switched to a main
    // variant that lists no addons while "allow skip" is off, so the old radio
    // stays checked). Don't resolve it to a variant — otherwise it becomes a
    // phantom cart line that isn't reflected in the displayed bundle price
    // (getSelectedPriceCents already returns 0 for a hidden tile).
    if (!tile || tile.hidden) {
      this.setResolvedVariantId(null);
      return Promise.resolve();
    }

    // Variant references already point at a specific variant; no size matching.
    var directVariantId = tile.dataset.tileVariantId;
    if (directVariantId) {
      this.setResolvedVariantId(directVariantId);
      this.updateSizeInfo('', null);
      return Promise.resolve();
    }

    // Product reference — resolve to the addon's variant matching the main size.
    var tileVariants = [];
    try { tileVariants = JSON.parse(tile.dataset.productVariants || '[]'); } catch (e) {}

    var mainVariantId = getMainVariantId();
    if (!mainVariantId) {
      var any = tileVariants.find(function (v) { return v.available; });
      this.setResolvedVariantId(any ? any.id : null);
      return Promise.resolve();
    }

    return fetchVariant(mainVariantId).then(function (mainVariant) {
      var mainSize = getMainVariantSize(mainVariant);
      var match = null;

      if (mainSize) {
        var normalized = normalizeSize(mainSize);
        match = tileVariants.find(function (v) {
          if (!v.available) return false;
          var opts = [v.option1, v.option2, v.option3];
          return opts.some(function (o) { return o && normalizeSize(o) === normalized; });
        });
      }

      if (!match) {
        match = tileVariants.find(function (v) { return v.available; });
      }

      if (match) {
        self.setResolvedVariantId(match.id);
        self.updateSizeInfo('matched', mainSize);
      } else {
        self.setResolvedVariantId(null);
        self.updateSizeInfo('missing', mainSize);
      }
    });
  };

  // ---------- SINGLE-PRODUCT MODE (legacy) ----------

  ProductAddonsBlock.prototype.syncSingle = function () {
    var self = this;
    var checked = this.getCheckedInput();

    if (!checked || checked.dataset.addonSkip === 'true') {
      this.setResolvedVariantId(null);
      return Promise.resolve();
    }

    if (!this.matchSize) {
      var directId = checked.dataset.addonVariantId;
      this.setResolvedVariantId(directId || null);
      return Promise.resolve();
    }

    var mainVariantId = getMainVariantId();
    if (!mainVariantId) return Promise.resolve();

    return fetchVariant(mainVariantId).then(function (mainVariant) {
      var mainSize = getMainVariantSize(mainVariant);
      if (!mainSize) {
        self.setResolvedVariantId(null);
        return;
      }
      var normalized = normalizeSize(mainSize);
      var selection = checked.dataset.addonSelection;

      var match = self.variants.find(function (v) {
        if (!v.available) return false;
        var size;
        if (self.sizeIndex === 1) size = v.option1;
        else if (self.sizeIndex === 2) size = v.option2;
        else if (self.sizeIndex === 3) size = v.option3;
        else size = null;
        if (size && normalizeSize(size) !== normalized) return false;

        if (self.selectableIndex > 0 && selection) {
          var sel;
          if (self.selectableIndex === 1) sel = v.option1;
          else if (self.selectableIndex === 2) sel = v.option2;
          else if (self.selectableIndex === 3) sel = v.option3;
          if (sel !== selection) return false;
        }
        return true;
      });

      if (match) {
        self.setResolvedVariantId(match.id);
        self.updateSizeInfo('matched', mainSize);
      } else {
        self.setResolvedVariantId(null);
        self.updateSizeInfo('missing', mainSize);
      }
    });
  };

  // ---------- Registry ----------

  var blocks = [];

  function init() {
    document.querySelectorAll('[data-product-addons-block]').forEach(function (el) {
      blocks.push(new ProductAddonsBlock(el));
    });
  }

  function syncAll() {
    return Promise.all(blocks.map(function (b) { return b.sync(); }));
  }

  // ---------- Bundle price (main product + selected addons) ----------

  var lastBundleTotal = null;
  var priceObserver = null;

  function getMainPriceEl() {
    var els = document.querySelectorAll('[id^="price-"]');
    for (var i = 0; i < els.length; i++) {
      if (!els[i].closest('.product__popup') && !els[i].closest('floated-form')) {
        return els[i];
      }
    }
    return els[0] || null;
  }

  function formatBundleMoney(cents) {
    if (typeof formatMoney !== 'function') return null;
    var fmt = (window.theme && window.theme.moneyFormat) || '';
    return formatMoney(cents, fmt);
  }

  function applyBundlePriceToDOM(totalCents) {
    var priceEl = getMainPriceEl();
    if (!priceEl) return;
    var formatted = formatBundleMoney(totalCents);
    if (formatted == null) return;
    var spans = priceEl.querySelectorAll('.price-item--regular, .price-item--sale');
    spans.forEach(function (s) {
      if (s.closest('s')) return; // leave the strike-through compare-at price alone
      if (s.textContent.trim() !== formatted.trim()) s.textContent = formatted;
    });
  }

  function updateBundlePrice() {
    var mainId = getMainVariantId();
    if (!mainId) return Promise.resolve();
    return fetchVariant(mainId).then(function (mainVariant) {
      if (!mainVariant || typeof mainVariant.price !== 'number') return;
      var total = mainVariant.price;
      blocks.forEach(function (b) {
        total += (typeof b.getSelectedPriceCents === 'function') ? b.getSelectedPriceCents() : 0;
      });
      lastBundleTotal = total;
      applyBundlePriceToDOM(total);
    });
  }

  // The theme re-renders #price-… on variant change, wiping our total.
  // Re-apply whenever it changes (guarded against its own writes).
  function observeMainPrice() {
    var priceEl = getMainPriceEl();
    if (!priceEl || priceObserver) return;
    priceObserver = new MutationObserver(function () {
      if (lastBundleTotal == null) return;
      var formatted = formatBundleMoney(lastBundleTotal);
      if (formatted == null) return;
      var spans = priceEl.querySelectorAll('.price-item--regular, .price-item--sale');
      var needsUpdate = false;
      spans.forEach(function (s) {
        if (s.closest('s')) return;
        if (s.textContent.trim() !== formatted.trim()) needsUpdate = true;
      });
      if (needsUpdate) applyBundlePriceToDOM(lastBundleTotal);
    });
    priceObserver.observe(priceEl, { childList: true, subtree: true, characterData: true });
  }

  function getCartItems(mainProductTitle) {
    var items = [];
    blocks.forEach(function (b) {
      var checked = b.getCheckedInput();
      if (!checked || checked.dataset.addonSkip === 'true') return;
      // Skip a checked addon whose tile is hidden (not valid for the current
      // main variant) — mirrors the guard in getSelectedPriceCents so the cart
      // never gets charged for an addon the shopper can't see / didn't price.
      var checkedTile = checked.closest('[data-addon-tile]');
      if (checkedTile && checkedTile.hidden) return;
      var variantId = parseInt(b.el.dataset.resolvedVariantId, 10);
      if (!variantId) return;
      var props = {};
      if (mainProductTitle) props['_Bundled with'] = mainProductTitle;
      items.push({
        id: variantId,
        quantity: 1,
        properties: props
      });
    });
    return items;
  }

  function watchVariantChanges() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var onMainVariantChange = function () {
      // Invalidate the stale total so the price observer doesn't re-apply
      // the previous variant's bundle price before we recompute it.
      lastBundleTotal = null;
      setTimeout(function () {
        syncAll().then(updateBundlePrice);
      }, 50);
    };

    var idInput = form.querySelector('[name="id"]');
    if (idInput) {
      var observer = new MutationObserver(onMainVariantChange);
      observer.observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('input[name="id"]') ||
          t.matches('select[name="id"]') ||
          t.matches('input[type="radio"][name*="option"]') ||
          t.matches('select[name*="option"]') ||
          (t.closest && t.closest('.product-form__input'))) {
        onMainVariantChange();
      }
    });

    document.addEventListener('variant:change', onMainVariantChange);
    document.addEventListener('variantChange', onMainVariantChange);
  }

  function start() {
    init();
    watchVariantChanges();
    observeMainPrice();
    syncAll().then(updateBundlePrice);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  function debug() {
    var mainVariantId = getMainVariantId();
    console.group('[ProductAddons] debug');
    console.log('main variant id (from form):', mainVariantId);
    blocks.forEach(function (b, idx) {
      console.group('block #' + idx + ' (' + b.blockId + ', mode=' + b.mode + ')');
      if (b.mode === 'metafield') {
        var allowed = (mainVariantId && b.visibilityMap[mainVariantId]) || [];
        console.log('visibility map:', b.visibilityMap);
        console.log('allowed ids for current main variant:', allowed);
        var tilesInfo = [];
        b.tiles.forEach(function (t) {
          tilesInfo.push({
            productId: t.dataset.productId,
            tileVariantId: t.dataset.tileVariantId || null,
            isVariantRef: t.dataset.isVariantRef,
            hidden: t.hidden
          });
        });
        console.table(tilesInfo);
      }
      console.log('resolved variant id:', b.el.dataset.resolvedVariantId);
      console.groupEnd();
    });
    console.groupEnd();
    return { mainVariantId: mainVariantId, blocks: blocks };
  }

  window.ProductAddons = {
    syncAll: syncAll,
    getCartItems: getCartItems,
    blocks: blocks,
    debug: debug
  };
})();
